import * as React from 'react';

import { useRouter } from 'next/router';

import { useAuth0 } from '@auth0/auth0-react';
import { Typography } from '@mui/material';
import { Stack } from '@mui/system';

import { ButtonPrimary, ButtonSecondary } from '@itp/talent/components/core/button';
import { Checkbox } from '@itp/talent/components/core/checkbox';
import { PasswordField } from '@itp/talent/components/core/passwordField';
import { TextField } from '@itp/talent/components/core/textField';
import { Form } from '@itp/talent/components/form';
import { PRIVATE_API_URL } from '@itp/talent/constants/config';
import { useLocalAuth } from '@itp/talent/context/localAuthContext';

import { Auth0Icon } from '../components/auth0Icon';
import { AuthCard } from '../components/authCard';
import { AuthDivider } from '../components/authDivider';
import { AuthFooterLinks } from '../components/authFooterLinks';
import { AuthHeading } from '../components/authHeading';
import { AuthSubheading } from '../components/authSubheading';
import { SocialMediaButtons } from '../components/socialMediaButtons';
import { useAuthRedirectGuard } from '../components/useAuthRedirectGuard';
import { SignUpFormSchema } from './signUpPage.schema';

type FormValues = {
  confirmPassword: string;
  email: string;
  name: string;
  password: string;
  terms: boolean;
};

const INITIAL_VALUES: FormValues = {
  confirmPassword: '',
  email: '',
  name: '',
  password: '',
  terms: false,
};

export const SignUpPage: React.FC = () => {
  const { isPageReady } = useAuthRedirectGuard();

  const { loginWithRedirect } = useAuth0();

  const [serverError, setServerError] = React.useState<null | string>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [registeredEmail, setRegisteredEmail] = React.useState<null | string>(null);

  const onSubmit = async (values: FormValues) => {
    if (isSubmitting) return;

    setServerError(null);
    setIsSubmitting(true);

    try {
      const res = await fetch(`${PRIVATE_API_URL}/auth/local/register`, {
        body: JSON.stringify({
          email: values.email,
          name: values.name,
          password: values.password,
        }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });

      const data = await res.json();

      if (!res.ok) {
        setServerError(data?.error ?? 'Registration failed');
        return;
      }

      setRegisteredEmail(values.email);
    } catch {
      setServerError('Network error, please try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isPageReady) return null;

  if (registeredEmail) {
    return (
      <AuthCard>
        <AuthHeading>Check your inbox</AuthHeading>
        <AuthSubheading variant="body2">
          We sent a verification link to <b>{registeredEmail}</b>. Click the link to activate your
          account.
        </AuthSubheading>
        <AuthDivider />
        <AuthFooterLinks leftAction={{ href: '/sign-in', label: 'Back to login' }} />
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <AuthHeading>Sign up</AuthHeading>
      <AuthSubheading variant="body2">Create your account in a minute.</AuthSubheading>

      <Form
        defaultValues={INITIAL_VALUES}
        error={serverError ? { message: serverError, name: 'email', type: 'manual' } : undefined}
        id="signUpForm"
        onSubmit={onSubmit}
        schema={SignUpFormSchema}
      >
        <Stack gap={3}>
          <TextField autoComplete="name" id="name" label="Full name" name="name" required />

          <TextField autoComplete="email" id="email" label="Email address" name="email" required />

          <PasswordField
            autoComplete="new-password"
            id="password"
            label="Password"
            name="password"
            required
          />

          <PasswordField
            autoComplete="new-password"
            id="confirmPassword"
            label="Confirm password"
            name="confirmPassword"
            required
          />

          <Checkbox
            formProps={{
              label: (
                <div>
                  <span>I accept the </span>
                  <a
                    href="https://talentprofile.net/terms"
                    rel="nofollow external noopener noreferrer"
                    target="_blank"
                  >
                    terms of use
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://talentprofile.net/privacy"
                    rel="nofollow external noopener noreferrer"
                    target="_blank"
                  >
                    privacy policy
                  </a>
                </div>
              ),
              name: 'terms',
            }}
          />

          <ButtonPrimary disabled={isSubmitting} type="submit">
            {isSubmitting ? 'Signing up…' : 'Sign up'}
          </ButtonPrimary>
        </Stack>
      </Form>

      <AuthDivider />

      <SocialMediaButtons>
        <ButtonSecondary onClick={() => loginWithRedirect()} sx={{ gap: 1 }} type="button">
          <Auth0Icon />
        </ButtonSecondary>
      </SocialMediaButtons>

      <AuthDivider />

      <AuthFooterLinks
        leftAction={{ href: '/sign-in', label: 'Already have an account? Log in' }}
      />
    </AuthCard>
  );
};
